import React, { useState } from 'react';
import { 
  HeartIcon, 
  HandThumbUpIcon, 
  ChatBubbleLeftIcon, 
  StarIcon,
  FlagIcon,
  EllipsisHorizontalIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  MegaphoneIcon,
  BellIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid, HandThumbUpIcon as HandThumbUpIconSolid } from '@heroicons/react/24/solid';
import { useFeed } from '../hooks/useFeed';
import { useAuth } from '../contexts/AuthContext';
import { FeedPost } from '../types';
import { useUsers } from '../hooks/useUsers';
import ConfirmationModal from './ConfirmationModal';
import UserProfileLink from './UserProfileLink';

interface PostCardProps {
  post: FeedPost;
}

const PostCard: React.FC<PostCardProps> = ({ post }) => {
  const { currentUser } = useAuth();
  const { addReaction, removeReaction, markAsRead, togglePin, deletePost, addComment } = useFeed();
  const { users, getUserDisplayName, getUserAvatar, fetchUsers } = useUsers();
  
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [showActions, setShowActions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [commentSuccess, setCommentSuccess] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  const authorName = getUserDisplayName(post.authorId);
  const authorAvatar = getUserAvatar(post.authorId);
  
  // Use stored authorName as fallback if getUserDisplayName returns "Unknown User"
  const displayName = authorName === 'Unknown User' && post.authorName ? post.authorName : authorName;
  
  // More comprehensive debugging
  console.log('PostCard Debug:', {
    postId: post.id,
    authorId: post.authorId,
    storedAuthorName: post.authorName,
    storedAuthorRole: post.authorRole,
    fetchedAuthorName: authorName,
    finalDisplayName: displayName,
    usersCount: Object.keys(users).length,
    users: Object.keys(users),
    hasUserData: !!users[post.authorId],
    userData: users[post.authorId]
  });
  
  const isAuthor = currentUser?.uid === post.authorId;
  const canManage = currentUser && ['author', 'admin'].includes(currentUser.role) && currentUser.status !== 'pending';
  const canDeleteOwnPost = currentUser && isAuthor; // Allow users to delete their own posts
  const canDeleteAnyPost = currentUser && currentUser.role === 'admin' && currentUser.status !== 'pending'; // Allow admins to delete any post
  
  // TEMPORARY: For testing admin functionality - remove this in production
  const isTestAdmin = currentUser && currentUser.email === 'admin@test.com'; // Replace with your actual admin email
  const isLocalStorageAdmin = localStorage.getItem('forceAdminRole') === 'true'; // Temporary admin override
  const canDeleteAnyPostTest = canDeleteAnyPost || isTestAdmin || isLocalStorageAdmin;
  const hasReacted = currentUser && post.reactions[currentUser.uid]; // Check if user has any reaction
  const reactionCount = Object.keys(post.reactions).length;
  const commentCount = post.comments.length;
  const isRead = currentUser && post.readBy.includes(currentUser.uid);

  // Fetch user data for the post author
  React.useEffect(() => {
    if (post.authorId) {
      fetchUsers([post.authorId]);
    }
  }, [post.authorId, fetchUsers]);

  // Fetch user data for the post author
  React.useEffect(() => {
    if (post.authorId) {
      fetchUsers([post.authorId]);
    }
  }, [post.authorId, fetchUsers]);

  // Mark post as read when viewed
  React.useEffect(() => {
    if (currentUser && !isRead) {
      markAsRead(post.id!);
    }
  }, [currentUser, isRead, post.id, markAsRead]);

  // Log admin permissions for debugging
  React.useEffect(() => {
    if (currentUser) {
      console.log('PostCard Debug - User Info:', {
        userId: currentUser.uid,
        userRole: currentUser.role,
        userStatus: currentUser.status,
        canDeleteAnyPost,
        canDeleteAnyPostTest,
        canDeleteOwnPost,
        canManage,
        postId: post.id,
        postAuthorId: post.authorId,
        isAuthor,
        isTestAdmin
      });
    }
  }, [currentUser, canDeleteAnyPost, canDeleteAnyPostTest, canDeleteOwnPost, canManage, post.id, post.authorId, isAuthor, isTestAdmin]);

  // Reset success message after 3 seconds
  React.useEffect(() => {
    if (commentSuccess) {
      const timer = setTimeout(() => {
        setCommentSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [commentSuccess]);

  const handleReaction = async () => {
    if (!currentUser || isLiking) return;
    
    setIsLiking(true);
    try {
      if (hasReacted) {
        // User has already liked, so unlike
        await removeReaction(post.id!);
      } else {
        // User hasn't liked, so like
        await addReaction(post.id!, 'like');
      }
    } catch (error) {
      console.error('Error handling reaction:', error);
    } finally {
      setIsLiking(false);
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !currentUser) return;

    setIsSubmitting(true);
    try {
      console.log('Submitting comment:', { postId: post.id, currentUser, newComment });
      
      // Create comment data
      const commentData = {
        authorId: currentUser.uid,
        authorName: currentUser.name || currentUser.email || 'Unknown User',
        authorRole: currentUser.role,
        content: newComment.trim()
      };

      console.log('Comment data:', commentData);

      // Add comment to the post
      await addComment(post.id!, commentData);
      
      console.log('Comment added successfully');
      
      // Clear the comment input
      setNewComment('');
      setCommentSuccess(true); // Set success state
      
      // Force a small delay to allow Firestore to update
      setTimeout(() => {
        // This will trigger a re-render and show the new comment
        setShowComments(false);
        setShowComments(true);
      }, 100);
    } catch (error) {
      console.error('Error submitting comment:', error);
      // You could add a toast notification here for user feedback
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      // Additional security check
      if (!canDeleteOwnPost && !canDeleteAnyPostTest) {
        console.error('User does not have permission to delete this post');
        return;
      }
      
      console.log('Deleting post:', { 
        postId: post.id, 
        postTitle: post.title, 
        isAdminDelete: canDeleteAnyPostTest && !canDeleteOwnPost,
        currentUserRole: currentUser?.role 
      });
      
      await deletePost(post.id!);
      setShowDeleteConfirm(false);
      setDeleteSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setDeleteSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'announcement': return <MegaphoneIcon className="h-4 w-4" />;
      case 'class_schedule': return <CalendarIcon className="h-4 w-4" />;
      case 'reminder': return <BellIcon className="h-4 w-4" />;
      case 'event': return <StarIcon className="h-4 w-4" />;
      default: return <ChatBubbleLeftIcon className="h-4 w-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={`bg-white rounded-xl shadow-lg border-2 transition-all duration-200 hover:shadow-xl max-w-full overflow-hidden ${
      post.isPinned ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200'
    } ${!isRead ? 'ring-2 ring-primary-200' : ''}`}>
      {/* Header */}
      <div className="p-3 sm:p-6 border-b border-gray-100">
        {/* Success Message */}
        {deleteSuccess && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg text-sm">
            {canDeleteAnyPostTest && !canDeleteOwnPost 
              ? `Post by ${displayName} deleted successfully as admin!` 
              : 'Post deleted successfully!'
            }
          </div>
        )}
        
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
            {/* Author Avatar */}
            {authorAvatar ? (
              <img
                src={authorAvatar}
                alt={displayName}
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-gray-200 flex-shrink-0"
              />
            ) : (
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-medium text-lg flex-shrink-0">
                {displayName.charAt(0).toUpperCase()}
              </div>
            )}
            
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                <UserProfileLink 
                  userId={post.authorId}
                  fallbackName={displayName}
                  className="font-semibold text-gray-900 text-sm sm:text-base truncate hover:text-primary-600"
                />
                {post.isAutomated && (
                  <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full whitespace-nowrap">
                    Automated
                  </span>
                )}
                {post.isPinned && (
                  <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full flex items-center whitespace-nowrap">
                    <StarIcon className="h-3 w-3 mr-1" />
                    Pinned
                  </span>
                )}
                {canDeleteAnyPostTest && !canDeleteOwnPost && (
                  <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full flex items-center border border-red-200 whitespace-nowrap">
                    <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                    Admin View
                  </span>
                )}
              </div>
              
              <div className="flex flex-wrap items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-500">
                <span className="whitespace-nowrap">{formatDate(post.createdAt)}</span>
                <span className="hidden sm:inline">•</span>
                <span className="capitalize whitespace-nowrap">{post.type.replace('_', ' ')}</span>
                {post.programFilter && (
                  <>
                    <span className="hidden sm:inline">•</span>
                    <span className="font-medium whitespace-nowrap">{post.programFilter}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Actions Menu */}
          <div className="relative">
            {canDeleteAnyPostTest && !canDeleteOwnPost && (
              <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-medium shadow-lg" title="Admin View - Can delete any post">
                A
              </div>
            )}
            <button
              onClick={() => setShowActions(!showActions)}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <EllipsisHorizontalIcon className="h-5 w-5" />
            </button>
            
            {showActions && (
              <div className="absolute right-0 top-full mt-2 w-40 sm:w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-10 max-w-[calc(100vw-2rem)]">
                {/* Debug info - remove this later */}
                <div className="px-3 sm:px-4 py-2 text-xs text-gray-500 border-b border-gray-100 bg-gray-50">
                  <div className="text-xs">
                    Debug: Role={currentUser?.role}, Status={currentUser?.status}
                  </div>
                  <button
                    onClick={() => {
                      const current = localStorage.getItem('forceAdminRole') === 'true';
                      localStorage.setItem('forceAdminRole', (!current).toString());
                      window.location.reload();
                    }}
                    className="mt-1 px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 w-full"
                  >
                    {localStorage.getItem('forceAdminRole') === 'true' ? 'Disable' : 'Enable'} Admin Mode
                  </button>
                </div>
                
                {canManage && (
                  <button
                    onClick={() => togglePin(post.id!)}
                    className="w-full px-3 sm:px-4 py-2 text-left text-xs sm:text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                  >
                    <StarIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                    <span className="truncate">{post.isPinned ? 'Unpin' : 'Pin'} Post</span>
                  </button>
                )}
                {(canDeleteOwnPost || canDeleteAnyPostTest) && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="w-full px-3 sm:px-4 py-2 text-left text-xs sm:text-sm text-red-600 hover:bg-red-50 flex items-center"
                  >
                    <TrashIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                    <span className="truncate">
                      {canDeleteAnyPostTest && !canDeleteOwnPost ? 'Delete Post (Admin Action)' : 'Delete Post'}
                    </span>
                  </button>
                )}
                {canDeleteAnyPostTest && !canDeleteOwnPost && (
                  <div className="px-3 sm:px-4 py-2 text-xs font-medium text-red-600 border-t border-gray-100 bg-red-50">
                    ⚠️ Admin Actions - You can delete any post
                  </div>
                )}
                <button className="w-full px-3 sm:px-4 py-2 text-left text-xs sm:text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                  <FlagIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span className="truncate">Report Post</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-3 sm:p-6">
        {/* Priority Badge */}
        <div className="mb-4">
          <span className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(post.priority)}`}>
            <ExclamationTriangleIcon className="h-3 w-3 mr-1 flex-shrink-0" />
            <span className="whitespace-nowrap">{post.priority.charAt(0).toUpperCase() + post.priority.slice(1)} Priority</span>
          </span>
        </div>

        {/* Title and Content */}
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 break-words">{post.title}</h2>
        <div className="prose prose-gray max-w-none">
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap break-words text-sm sm:text-base">{post.content}</p>
        </div>

        {/* Tags */}
        {post.tags.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1 sm:gap-2">
            {post.tags.map((tag, index) => (
              <span
                key={index}
                className="px-2 sm:px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full font-medium whitespace-nowrap"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Attachments */}
        {post.attachments && post.attachments.length > 0 && (
          <div className="mt-4 p-3 sm:p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-900 mb-2">Attachments</h4>
            <div className="flex flex-wrap gap-2">
              {post.attachments.map((attachment, index) => (
                <div
                  key={index}
                  className="px-2 sm:px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs sm:text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors break-all"
                >
                  📎 {attachment}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Actions Bar */}
      <div className="px-3 sm:px-6 py-3 sm:py-4 border-t border-gray-100 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Reactions */}
            <div className="flex items-center space-x-1 sm:space-x-2">
              <button
                onClick={handleReaction}
                disabled={isLiking}
                title={hasReacted ? "Unlike this post" : "Like this post"}
                aria-label={hasReacted ? "Unlike this post" : "Like this post"}
                className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-2 rounded-lg transition-all duration-200 ${
                  hasReacted 
                    ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' 
                    : 'text-gray-500 hover:text-blue-600 hover:bg-gray-100'
                } ${isLiking ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {isLiking ? (
                  <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                ) : hasReacted ? (
                  <HandThumbUpIconSolid className="h-4 w-4 sm:h-5 sm:w-5" />
                ) : (
                  <HandThumbUpIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                )}
                {reactionCount > 0 && (
                  <span className="text-xs sm:text-sm font-medium">{reactionCount}</span>
                )}
              </button>
            </div>

            {/* Comments */}
            <button
              onClick={() => setShowComments(!showComments)}
              className="flex items-center space-x-1 sm:space-x-2 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChatBubbleLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="text-xs sm:text-sm">{commentCount} comments</span>
            </button>
          </div>

          {/* Expiry Info */}
          {post.expiresAt && (
            <div className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">
              Expires {formatDate(post.expiresAt)}
            </div>
          )}
        </div>
      </div>

      {/* Comments Section */}
      {showComments && (
        <div className="border-t border-gray-100 bg-gray-50">
          <div className="p-3 sm:p-6">
            <h4 className="font-medium text-gray-900 mb-4 text-sm sm:text-base">Comments</h4>
            
            {/* Comment Form */}
            {currentUser && (
              <form onSubmit={handleSubmitComment} className="mb-6">
                {/* Success Message */}
                {commentSuccess && (
                  <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg text-sm">
                    Comment posted successfully!
                  </div>
                )}
                
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                    disabled={isSubmitting}
                  />
                  <button
                    type="submit"
                    disabled={!newComment.trim() || isSubmitting}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap"
                  >
                    {isSubmitting ? 'Posting...' : 'Post'}
                  </button>
                </div>
              </form>
            )}

            {/* Comments List */}
            <div className="space-y-3 sm:space-y-4">
              {post.comments.length === 0 ? (
                <p className="text-gray-500 text-center py-4 text-sm">No comments yet. Be the first to comment!</p>
              ) : (
                post.comments.map((comment, index) => (
                  <div key={index} className="flex space-x-2 sm:space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center text-white text-xs sm:text-sm font-medium">
                        {comment.authorName.charAt(0).toUpperCase()}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="bg-white rounded-lg px-3 sm:px-4 py-2 sm:py-3 shadow-sm">
                        <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-2">
                          <span className="text-xs sm:text-sm font-semibold text-gray-900 truncate">{comment.authorName}</span>
                          <span className="text-xs text-gray-500 whitespace-nowrap">{formatDate(comment.createdAt)}</span>
                          {comment.isEdited && (
                            <span className="text-xs text-gray-400">(edited)</span>
                          )}
                        </div>
                        <p className="text-xs sm:text-sm text-gray-700 break-words">{comment.content}</p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Post"
        message={
          canDeleteAnyPostTest && !canDeleteOwnPost
            ? `Are you sure you want to delete this post by ${displayName} as an admin? This action cannot be undone and will remove the post from all users' feeds.`
            : "Are you sure you want to delete this post? This action cannot be undone."
        }
        confirmText={canDeleteAnyPostTest && !canDeleteOwnPost ? "Delete Post (Admin Action)" : "Delete Post"}
        confirmButtonColor="red"
      />
    </div>
  );
};

export default PostCard;
